/**
 * Utility for extracting human-readable, detailed error messages from API errors.
 * 
 * Handles:
 * 1. Standard CCMS ApiResponse envelopes:
 *    { success: false, message: "...", errors: [...] }
 * 2. ASP.NET RFC 7807 ProblemDetails / ValidationProblemDetails:
 *    { title: "...", detail: "...", errors: { field: ["..."] } }
 * 3. JSON strings or raw error strings
 * 4. Error objects, while specifically preventing Axios's default
 *    "Request failed with status code 400/404/500" from masking the real server reason.
 */

function isGenericMessage(text: string): boolean {
  const lower = text.trim().toLowerCase();
  return (
    lower === 'bad request.' ||
    lower === 'bad request' ||
    lower === 'an error occurred.' ||
    lower === 'an error occurred' ||
    lower === 'one or more validation errors occurred.' ||
    lower === 'validation failed' ||
    lower === 'validation error' ||
    lower === 'request failed.' ||
    lower === 'request failed' ||
    isAxiosGenericStatusMessage(text)
  );
}

function isAxiosGenericStatusMessage(text: string): boolean {
  return /^Request failed with status code \d+$/i.test(text.trim());
}

export function extractApiErrorMessage(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (!err) return fallback;

  // 1. If err is an object that may contain an HTTP response (e.g. AxiosError)
  const maybeResponse = (err as {
    response?: {
      data?: unknown;
      status?: number;
      statusText?: string;
    };
  })?.response;

  if (maybeResponse && maybeResponse.data !== undefined && maybeResponse.data !== null) {
    let data: unknown = maybeResponse.data;

    // Handle case where response.data was not parsed as JSON (e.g. string or text/plain)
    if (typeof data === 'string') {
      const trimmed = data.trim();
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          data = JSON.parse(trimmed);
        } catch {
          if (!trimmed.toLowerCase().startsWith('<!doctype') && !trimmed.toLowerCase().startsWith('<html')) {
            return trimmed;
          }
        }
      } else if (!trimmed.toLowerCase().startsWith('<!doctype') && !trimmed.toLowerCase().startsWith('<html')) {
        return trimmed;
      }
    }

    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;

      const message =
        typeof obj.message === 'string' && obj.message.trim()
          ? obj.message.trim()
          : typeof obj.Message === 'string' && obj.Message.trim()
            ? obj.Message.trim()
            : null;

      const detail =
        typeof obj.detail === 'string' && obj.detail.trim()
          ? obj.detail.trim()
          : typeof obj.Detail === 'string' && obj.Detail.trim()
            ? obj.Detail.trim()
            : null;

      const title =
        typeof obj.title === 'string' && obj.title.trim()
          ? obj.title.trim()
          : typeof obj.Title === 'string' && obj.Title.trim()
            ? obj.Title.trim()
            : null;

      const errorField =
        typeof obj.error === 'string' && obj.error.trim()
          ? obj.error.trim()
          : typeof obj.Error === 'string' && obj.Error.trim()
            ? obj.Error.trim()
            : null;

      // Extract validation errors
      const rawErrors = obj.errors ?? obj.Errors;
      const formattedErrors: string[] = [];

      if (Array.isArray(rawErrors)) {
        for (const item of rawErrors) {
          if (typeof item === 'string' && item.trim()) {
            formattedErrors.push(item.trim());
          } else if (item && typeof item === 'object') {
            const errObj = item as Record<string, unknown>;
            const msg = errObj.errorMessage || errObj.message || errObj.error;
            if (typeof msg === 'string' && msg.trim()) {
              formattedErrors.push(msg.trim());
            }
          }
        }
      } else if (rawErrors && typeof rawErrors === 'object') {
        // e.g. { "Budget": ["Budget must be greater than 0"], "StartDate": ["Cannot be in the past"] }
        for (const [key, val] of Object.entries(rawErrors as Record<string, unknown>)) {
          if (Array.isArray(val)) {
            for (const v of val) {
              if (typeof v === 'string' && v.trim()) {
                formattedErrors.push(key && key !== '$' ? `${key}: ${v.trim()}` : v.trim());
              }
            }
          } else if (typeof val === 'string' && val.trim()) {
            formattedErrors.push(key && key !== '$' ? `${key}: ${val.trim()}` : val.trim());
          }
        }
      }

      // If we have detailed errors
      if (formattedErrors.length > 0) {
        const joinedErrors = formattedErrors.join('; ');
        // If message is present and NOT generic, combine them
        if (message && !isGenericMessage(message)) {
          return `${message} (${joinedErrors})`;
        }
        return joinedErrors;
      }

      // If we have a specific message that is not generic
      if (message && !isGenericMessage(message)) {
        return message;
      }

      // If we have detail (RFC 7807 ProblemDetails)
      if (detail && !isGenericMessage(detail)) {
        return detail;
      }

      // Fallback to message even if somewhat generic
      if (message) {
        return message;
      }

      // If we have title (ProblemDetails)
      if (title && !isGenericMessage(title)) {
        return title;
      }

      // If we have error field
      if (errorField && !isGenericMessage(errorField)) {
        return errorField;
      }
    }
  }

  // 2. If err has a direct message property (e.g. Error or custom object)
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = String((err as { message: unknown }).message);
    if (msg && !isAxiosGenericStatusMessage(msg)) {
      return msg;
    }
  }

  // 3. If err is a plain string
  if (typeof err === 'string' && err.trim()) {
    if (!isAxiosGenericStatusMessage(err)) {
      return err.trim();
    }
  }

  return fallback;
}
